import { Alert, Divider, Skeleton, Stack, Text } from '@billda/design-system';
import { useLabourItemDetails } from '../../../hooks/useLabourItemDetails';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useAllocationsQuery } from '../../../hooks/useAllocationsQuery';
import { LabourItemStatusBadge } from '../atoms/LabourItemStatusBadge';
import { InfoRow } from '../atoms/InfoRow';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { useLabourItemEventsTimeline } from '../../../hooks/useLabourItemEventsTimeline';
import {
  getProgressTone,
  progressToneSurfaceClasses,
} from '../../v2/atoms/ProgressBar';
import { formatCurrency } from '../../../../../shared/utils/currency';
import {
  formatQuantity,
  formatUnitRate,
} from '../../../../../shared/utils/quantity';

interface LabourItemDetailsPanelProps {
  labourItemId: number;
}

const UnitRateDetailsSection = ({
  unitRate,
  totalQuantity,
  remainingQuantity,
  unitLabel,
}: {
  unitRate: number;
  totalQuantity: number;
  remainingQuantity: number;
  unitLabel: string;
}) => (
  <>
    <Divider className="my-4" />
    <Text variant="overline" className="mb-1 block font-semibold">
      Quantity pricing
    </Text>
    <InfoRow label="Rate" testId="unit-rate">
      {formatUnitRate(unitRate, unitLabel)}
    </InfoRow>
    <InfoRow label="Total quantity" testId="total-quantity">
      {formatQuantity(totalQuantity, unitLabel)}
    </InfoRow>
    <InfoRow label="Remaining quantity" testId="remaining-quantity">
      {formatQuantity(remainingQuantity, unitLabel)}
    </InfoRow>
  </>
);

export function LabourItemDetailsPanel({
  labourItemId,
}: LabourItemDetailsPanelProps) {
  const { siteId, plotId } = useSitePanelState();

  const siteIdNum = siteId ? parseInt(siteId, 10) : null;
  const plotIdNum = plotId ? parseInt(plotId, 10) : null;

  const { data: labourItem, error } = useLabourItemDetails(
    siteIdNum,
    plotIdNum,
    labourItemId
  );

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = useLabourItemEventsTimeline(siteIdNum, plotIdNum, labourItemId);

  const { allocations } = useAllocationsQuery(
    siteIdNum ?? 0,
    plotIdNum ?? 0,
    labourItemId,
    !!(siteIdNum && plotIdNum)
  );

  const workNotes = allocations
    .map(a => a.workNotes)
    .filter((note): note is string => !!note);

  if (error && !labourItem) {
    return (
      <div data-testid="labour-item-details-panel" className="p-6">
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load labour item details'}
        </Alert>
      </div>
    );
  }

  if (!labourItem) {
    return (
      <div data-testid="labour-item-details-panel" className="p-6">
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={180} />
        </Stack>
      </div>
    );
  }

  const { financialMetrics } = labourItem;

  return (
    <div data-testid="labour-item-details-panel">
      <div className="p-6 pb-0">
        <div className="flex items-start justify-between gap-2">
          <Text variant="h6" className="leading-tight">
            {labourItem.definitionName}
          </Text>
          <LabourItemStatusBadge status={labourItem.status} />
        </div>
      </div>

      <div className="p-6">
        <Stack spacing={0}>
          <Text variant="overline" className="mb-1 block font-semibold">
            Details
          </Text>
          <InfoRow label="Allocations" testId="allocations-count">
            {labourItem.allocationsCount}
          </InfoRow>
          {workNotes.length > 0 && (
            <InfoRow label="Work Notes" testId="work-notes" multiline>
              {workNotes.join('\n')}
            </InfoRow>
          )}

          <Divider className="my-4" />
          <Text variant="overline" className="mb-1 block font-semibold">
            Location
          </Text>
          <InfoRow label="Site" testId="location-site">
            {labourItem.context.siteName}
          </InfoRow>
          <InfoRow label="Plot" testId="location-plot">
            {labourItem.context.plotName}
          </InfoRow>
          <InfoRow label="Plot Number" testId="location-plot-number">
            {labourItem.context.plotNumber}
          </InfoRow>

          {labourItem.pricingMode === 'UnitRate' &&
            labourItem.unitRate != null &&
            labourItem.totalQuantity != null &&
            labourItem.remainingQuantity != null &&
            labourItem.unitLabel && (
              <UnitRateDetailsSection
                unitRate={labourItem.unitRate}
                totalQuantity={labourItem.totalQuantity}
                remainingQuantity={labourItem.remainingQuantity}
                unitLabel={labourItem.unitLabel}
              />
            )}

          {financialMetrics && (
            <>
              <Divider className="my-4" />
              <Text variant="overline" className="mb-1 block font-semibold">
                Financials
              </Text>
              <InfoRow label="Price" testId="price">
                {formatCurrency(financialMetrics.price)}
              </InfoRow>
              <InfoRow label="Booked" testId="booked-amount">
                {formatCurrency(financialMetrics.bookedAmount)}
              </InfoRow>
              <InfoRow label="Approved" testId="approved-amount">
                {formatCurrency(financialMetrics.approvedAmount)}
              </InfoRow>
              <InfoRow label="Remaining" testId="remaining">
                <Text
                  as="span"
                  variant="body2"
                  className={
                    financialMetrics.remaining >= 0
                      ? 'text-content-success'
                      : 'text-content-error'
                  }
                >
                  {formatCurrency(financialMetrics.remaining)}
                </Text>
              </InfoRow>
              <div className="mb-3">
                <div className="mb-1 flex justify-between">
                  <Text variant="body2" color="secondary">
                    Completion
                  </Text>
                  <Text variant="body2" className="font-semibold">
                    {labourItem.completionPercentage.toFixed(1)}%
                  </Text>
                </div>
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(
                    Math.min(labourItem.completionPercentage, 100)
                  )}
                  className="h-2 w-full overflow-hidden rounded-lg bg-surface-raised"
                >
                  <div
                    className={`h-full rounded-lg transition-[width] ${
                      progressToneSurfaceClasses[
                        labourItem.completionPercentage >= 100
                          ? 'error'
                          : getProgressTone(labourItem.completionPercentage)
                      ]
                    }`}
                    style={{
                      width: `${Math.min(labourItem.completionPercentage, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {siteIdNum && plotIdNum && (
            <>
              <Divider className="my-4" />
              <Text variant="overline" className="mb-1.5 block font-semibold">
                Activity
              </Text>
              <PagedActivityTimeline
                events={events}
                totalCount={timelineTotalCount}
                hasMore={timelineHasMore}
                isLoading={timelineLoading}
                isLoadingMore={timelineLoadingMore}
                loadMore={timelineLoadMore}
              />
            </>
          )}
        </Stack>
      </div>
    </div>
  );
}
